import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

import CalendarGrid from './components/CalendarGrid';
import DepartmentAvailability from './components/DepartmentAvailability';
import UpcomingDeadlines from './components/UpcomingDeadlines';
import ResourceConflicts from './components/ResourceConflicts';
import EventFilters from './components/EventFilters';
import { format, addMonths, subMonths } from 'date-fns';
import { supabase } from '../../lib/supabase';
import pngHolidaysService from '../../services/pngHolidaysService';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';

const StrategicWorkforceCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('monthly');
  const [selectedDepartments, setSelectedDepartments] = useState(['all']);
  const [selectedEventTypes, setSelectedEventTypes] = useState(['leave', 'events', 'deadlines', 'availability']);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  const viewModeOptions = [
    { value: 'monthly', label: 'Monthly View' },
    { value: 'weekly', label: 'Weekly View' },
    { value: 'daily', label: 'Daily View' }
  ];

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' },
    { value: 'operations', label: 'Operations' },
    { value: 'it', label: 'Information Technology' },
    { value: 'sales', label: 'Sales & Marketing' }
  ];

  useEffect(() => {
    loadEvents();
    checkSubscription();
  }, [currentDate, selectedDepartments, selectedEventTypes]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const allEvents = [];

      // Load PNG Gazette Holidays 2026 if events are selected
      if (selectedEventTypes?.includes('events')) {
        const pngHolidays = pngHolidaysService?.formatForCalendar();
        allEvents?.push(...pngHolidays);
      }

      // Load leave requests if selected
      if (selectedEventTypes?.includes('leave')) {
        const { data: leaveData } = await supabase
          ?.from('leave_requests')
          ?.select(`
            id,
            leave_type,
            start_date,
            end_date,
            leave_status,
            employees!inner(first_name, last_name, department)
          `)
          ?.eq('leave_status', 'approved');

        leaveData?.forEach(leave => {
          const deptMatch = selectedDepartments?.includes('all') || selectedDepartments?.includes(leave?.employees?.department?.toLowerCase());
          if (deptMatch) {
            allEvents?.push({
              id: `leave-${leave?.id}`,
              title: `${leave?.employees?.first_name} ${leave?.employees?.last_name} - ${leave?.leave_type} Leave`,
              type: 'leave',
              subtype: leave?.leave_type,
              date: new Date(leave?.start_date),
              endDate: new Date(leave?.end_date),
              department: leave?.employees?.department?.toLowerCase(),
              employeeName: `${leave?.employees?.first_name} ${leave?.employees?.last_name}`,
              status: leave?.leave_status,
              impact: 'medium'
            });
          }
        });
      }

      // Load deadlines if selected
      if (selectedEventTypes?.includes('deadlines')) {
        const { data: nasfundData } = await supabase
          ?.from('nasfund_contributions')
          ?.select('id, contribution_period, due_date, filing_status')
          ?.eq('filing_status', 'pending');

        nasfundData?.forEach(filing => {
          allEvents?.push({
            id: `nasfund-${filing?.id}`,
            title: 'NASFUND Submission Deadline',
            type: 'deadlines',
            date: new Date(filing?.due_date),
            department: 'all',
            priority: 'high',
            description: `${filing?.contribution_period} NASFUND contributions due`
          });
        });

        const { data: swtData } = await supabase
          ?.from('swt_filings')
          ?.select('id, filing_period, due_date, filing_status')
          ?.eq('filing_status', 'pending');

        swtData?.forEach(filing => {
          allEvents?.push({
            id: `swt-${filing?.id}`,
            title: 'SWT Filing Deadline',
            type: 'deadlines',
            date: new Date(filing?.due_date),
            department: 'all',
            priority: 'high',
            description: `${filing?.filing_period} Salary and Wages Tax filing`
          });
        });
      }

      setEvents(allEvents);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handlePreviousPeriod = () => {
    if (viewMode === 'monthly') {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (viewMode === 'weekly') {
      setCurrentDate(new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000));
    } else {
      setCurrentDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000));
    }
  };

  const handleNextPeriod = () => {
    if (viewMode === 'monthly') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (viewMode === 'weekly') {
      setCurrentDate(new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000));
    } else {
      setCurrentDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      // Simulate export generation
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const timestamp = format(new Date(), 'yyyy-MM-dd');
      const filename = `workforce-calendar-${timestamp}.pdf`;
      
      alert(`Exporting workforce calendar as ${filename}...`);
    } catch (error) {
      alert(`Export failed: ${error?.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const getCurrentPeriodLabel = () => {
    if (viewMode === 'monthly') {
      return format(currentDate, 'MMMM yyyy');
    } else if (viewMode === 'weekly') {
      return `Week of ${format(currentDate, 'MMM d, yyyy')}`;
    } else {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <LegalDisclaimerBanner />
      <GlobalFilterBar onFilterChange={() => {}} />
      <DataRefreshIndicator />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-[1600px] mx-auto">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                Strategic Workforce Calendar
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Comprehensive visibility into organizational scheduling and resource planning across all departments
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="default"
                onClick={handleExport}
                loading={isExporting}
                iconName="Download"
                iconPosition="left"
              >
                Export Report
              </Button>
              <Button
                variant="default"
                size="default"
                iconName="Plus"
                iconPosition="left"
              >
                Add Event
              </Button>
            </div>
          </div>

          {/* View Controls */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPeriod}
                iconName="ChevronLeft"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleToday}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPeriod}
                iconName="ChevronRight"
              />
              <span className="ml-2 font-medium text-foreground">
                {getCurrentPeriodLabel()}
              </span>
            </div>

            <div className="flex items-center gap-2 md:ml-auto">
              <Select
                value={viewMode}
                onChange={(e) => setViewMode(e?.target?.value)}
                options={viewModeOptions}
              />
            </div>
          </div>

          {/* Event Type Filters */}
          <EventFilters
            selectedTypes={selectedEventTypes}
            onTypeToggle={setSelectedEventTypes}
            selectedDepartments={selectedDepartments}
            onDepartmentsChange={setSelectedDepartments}
            departmentOptions={departmentOptions}
          />
        </div>

        {/* Calendar Grid */}
        <div className="mb-8">
          <CalendarGrid
            currentDate={currentDate}
            viewMode={viewMode}
            events={events}
            loading={loading}
          />
        </div>

        {/* Side Panels */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <DepartmentAvailability
              currentDate={currentDate}
              events={events}
              departments={departmentOptions}
            />
          </div>
          <div className="space-y-6">
            <UpcomingDeadlines events={events?.filter(e => e?.type === 'deadlines')} />
            <ResourceConflicts events={events} />
          </div>
        </div>
      </main>
      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default StrategicWorkforceCalendar;